import { useState } from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useEngagementMe } from '@/hooks/queries/useEngagement'
import { FOCUS_RING } from '@/lib/focus-ring'
import { cn } from '@/lib/utils'
import { useOrganizationStore } from '@/store/organization.store'
import { getOrgPath } from '@/utils/org-paths'
import { CaretRightIcon, FireIcon } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'

import { GoalRing } from './GoalRing'
import { WeekStrip } from './WeekStrip'

/**
 * Current streak in the web header and the desktop title bar. Hidden until
 * there is something to show (a streak or a goal) so it never nags; tinted
 * when today still needs one thing to keep the streak alive.
 */
export function StreakChip({ className }: { className?: string }) {
  const navigate = useNavigate()
  const orgSlug = useOrganizationStore(state => state.currentOrganization?.slug)
  const { data: me } = useEngagementMe()
  const [open, setOpen] = useState(false)

  if (!me) return null
  const { streak, week, goals } = me
  if (streak.current === 0 && goals.length === 0) return null

  const label = streak.atRiskToday
    ? `Do one thing today to keep your ${streak.current}-day streak`
    : `${streak.current}-day streak`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-streak-chip
          data-at-risk={streak.atRiskToday || undefined}
          aria-label={label}
          title={label}
          className={cn(
            'inline-flex h-8 shrink-0 items-center gap-1 rounded-full border px-2.5 text-[length:var(--font-size-sm)] font-semibold tabular-nums leading-none transition-colors',
            FOCUS_RING,
            streak.atRiskToday
              ? 'border-[hsl(var(--streak-flame)/0.35)] bg-[hsl(var(--streak-flame-soft))] text-[hsl(var(--streak-flame-ink))]'
              : 'border-border bg-background/80 text-foreground hover:bg-muted',
            className,
          )}
        >
          <FireIcon
            aria-hidden
            weight="fill"
            className="h-3.5 w-3.5 text-[hsl(var(--streak-flame))]"
          />
          {streak.current}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-4">
        <p className="text-[length:var(--font-size-sm)] font-semibold text-foreground">
          {streak.current === 1
            ? '1-day streak'
            : `${streak.current}-day streak`}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {streak.atRiskToday
            ? 'Finish one thing today to keep it.'
            : 'Days off never break it.'}
        </p>
        <WeekStrip
          className="mt-3"
          weekStart={week.weekStart}
          today={streak.today}
          activeDates={week.activeDates}
          workingDays={streak.workingDays}
          atRiskToday={streak.atRiskToday}
        />
        {goals.length > 0 && (
          <div className="mt-4 flex flex-col gap-2.5">
            {goals.slice(0, 3).map(goal => (
              <GoalRing key={goal.id} goal={goal} />
            ))}
          </div>
        )}
        <button
          type="button"
          className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          onClick={() => {
            setOpen(false)
            if (orgSlug) navigate(getOrgPath(orgSlug, '/achievements'))
          }}
        >
          Achievements
          <CaretRightIcon className="h-3 w-3" />
        </button>
      </PopoverContent>
    </Popover>
  )
}
